/**
 * Extraction-specific helpers for the generic BackgroundJobsProvider.
 *
 * No React context or wrapper component — just a helper function that builds
 * the BackgroundJobConfig for extraction polling, and a convenience hook.
 */

import { useCallback } from "react"
import { useBackgroundJobs, type BackgroundJobConfig, type PollResult } from "@/components/shared/BackgroundJobsProvider"
import { getExtractionProgress } from "@/services/extractionsService"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Polling interval for extraction jobs (milliseconds). */
const EXTRACTION_POLL_INTERVAL_MS = 10_000

/**
 * Maximum time (milliseconds) to poll a single extraction before giving up.
 * Matches the execute-extraction Lambda's 15-minute timeout — if the backend
 * hasn't finished by then, it never will.
 */
const EXTRACTION_TIMEOUT_MS = 15 * 60 * 1000

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert an S3 prefix to the server mount path for display in toasts. */
function toMountPath(prefix: string): string {
  return `/mnt/s3/${prefix.replace("s3://", "").replace(/\/$/, "")}`
}

/**
 * Build the job ID used in BackgroundJobsProvider for a given extraction session.
 * Prefixed to avoid collisions with other job types.
 *
 * @param sessionId - The chat session UUID.
 * @returns A unique job ID string.
 */
function extractionJobId(sessionId: string): string {
  return `extraction:${sessionId}`
}

/**
 * Build a BackgroundJobConfig for an extraction polling job.
 *
 * @param sessionId - The chat session UUID whose extraction is in flight.
 * @param isUpdate - Whether this is an update to an existing extraction.
 * @param onSettled - Optional callback invoked when the extraction completes or fails.
 * @returns A fully configured BackgroundJobConfig ready to submit.
 */
export function buildExtractionJobConfig(
  sessionId: string,
  isUpdate: boolean,
  onSettled?: () => void
): BackgroundJobConfig {
  return {
    id: extractionJobId(sessionId),
    loadingTitle: isUpdate ? "Updating extraction\u2026" : "Creating extraction\u2026",
    loadingDescription: "This may take a minute. You can continue working.",
    timeoutMs: EXTRACTION_TIMEOUT_MS,
    pollIntervalMs: EXTRACTION_POLL_INTERVAL_MS,
    onSettled,
    poll: async (accessToken: string): Promise<PollResult> => {
      const progress = await getExtractionProgress(sessionId, accessToken)

      if (progress.status === "COMPLETED") {
        const location = progress.s3Prefix ? toMountPath(progress.s3Prefix) : "S3"
        return {
          status: "completed",
          title: isUpdate ? "Extraction update complete." : "Extraction complete.",
          description: `Files available at: ${location}`,
        }
      }

      if (progress.status === "FAILED") {
        const errorMsg: string = progress.error || "Unknown error during extraction"
        return {
          status: "failed",
          title: isUpdate ? "Extraction update failed." : "Extraction failed.",
          description: errorMsg,
        }
      }

      // IN_PROGRESS or null — keep polling
      return { status: "pending" }
    },
  }
}

// ---------------------------------------------------------------------------
// Convenience Hook
// ---------------------------------------------------------------------------

/**
 * Hook providing extraction-specific shortcuts on top of useBackgroundJobs.
 *
 * Avoids the need for consumers to know about job ID formatting or config
 * construction — just call trackExtraction / isExtractionInProgress.
 *
 * @returns Object with trackExtraction and isExtractionInProgress functions.
 */
export function useExtractionPolling(): {
  trackExtraction: (sessionId: string, isUpdate: boolean, onSettled?: () => void) => void
  isExtractionInProgress: (sessionId: string) => boolean
} {
  const { submitJob, isJobInProgress } = useBackgroundJobs()

  /**
   * Start tracking an extraction. Submits a job to BackgroundJobsProvider
   * with the extraction-specific poll function, timeout, and toast config.
   *
   * @param sessionId - The chat session UUID whose extraction is in flight.
   * @param isUpdate - Whether this is an update to an existing extraction.
   * @param onSettled - Optional callback invoked when the extraction completes or fails.
   */
  const trackExtraction = useCallback(
    (sessionId: string, isUpdate: boolean, onSettled?: () => void): void => {
      submitJob(buildExtractionJobConfig(sessionId, isUpdate, onSettled))
    },
    [submitJob]
  )

  /**
   * Check if a specific session has an extraction in flight.
   *
   * @param sessionId - The session to check.
   * @returns True if the session has an extraction being tracked.
   */
  const isExtractionInProgress = useCallback(
    (sessionId: string): boolean => {
      return isJobInProgress(extractionJobId(sessionId))
    },
    [isJobInProgress]
  )

  return { trackExtraction, isExtractionInProgress }
}
